import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhImage";
import "@phosphor-icons/webcomponents/PhCurrencyEur";
import "@phosphor-icons/webcomponents/PhUsersThree";
import "@phosphor-icons/webcomponents/PhTicket";
import "@phosphor-icons/webcomponents/PhShare";

import "ui-components/modal/wow-modal";
import "ui-components/button/wow-button";
import "ui-components/status-badge/status-badge";
import "ui-components/expand-text/expand-text";

import { styles } from "./live-event-preview-modal.css";
import { t, translate } from "../../translations/i18n";
import { getAssetUrl } from "helpers/assetHelpers";
import { DataQueryController } from "ui-common/data-query-controller";
import { profileDataQueryOptions } from "../../queries/profile-data";

export type LiveEventStatus = "scheduled" | "live" | "ended" | "cancelled";

export interface LiveEventPreviewDetails {
  id: string;
  title: string;
  description?: string | null;
  thumbnail_url?: string | null;
  scheduled_start_time: Date | string;
  status: LiveEventStatus;
  shareUrl: string;
  fee_cents?: number | null;
  content_tier?: string;
  max_attendees?: number | null;
  cta_text?: string | null;
}

@customElement("live-event-preview-modal")
export class LiveEventPreviewModal extends LitElement {
  static override styles = [styles];

  @property({ type: Object })
  event!: LiveEventPreviewDetails;

  private profileDataQuery = new DataQueryController(this, () => profileDataQueryOptions);

  private get thumbnailUrl(): string | undefined {
    if (!this.event.thumbnail_url) return undefined;
    return getAssetUrl(this.event.thumbnail_url);
  }

  private get isPaidEvent(): boolean {
    return this.event.content_tier === "premium" && !!this.event.fee_cents;
  }

  private getThemePrimaryColor(): string | undefined {
    const profileData = this.profileDataQuery.result.data;
    if (!profileData) return undefined;
    return (
      profileData.theme?.primaryColor || profileData.primaryColor || profileData.customColors?.primary || undefined
    );
  }

  override updated() {
    const primaryColor = this.getThemePrimaryColor();
    if (primaryColor) {
      this.style.setProperty("--theme-primary-color", primaryColor);
    }
  }

  private handleClose() {
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true }));
  }

  private handleViewEventPage() {
    window.open(this.event.shareUrl, "_blank", "noopener,noreferrer");
  }

  private handleShareWithAudience() {
    this.dispatchEvent(
      new CustomEvent("share-with-audience", {
        detail: { event: this.event },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private getHeaderText(): string {
    switch (this.event.status) {
      case "live":
        return translate("liveEventPreviewModal.headerLive");
      case "scheduled":
        return translate("liveEventPreviewModal.headerScheduled");
      case "ended":
        return translate("liveEventPreviewModal.headerEnded");
      default:
        return translate("liveEventPreviewModal.header");
    }
  }

  private formatPrice(feeCents: number): string {
    return `${(feeCents / 100).toFixed(0)},-`;
  }

  private renderPriceBadge() {
    if (!this.isPaidEvent || !this.event.fee_cents) return nothing;

    return html`
      <status-badge variant="price" class="price-badge">
        <ph-currency-eur slot="icon" size="12" weight="bold"></ph-currency-eur>
        ${this.formatPrice(this.event.fee_cents)}
      </status-badge>
    `;
  }

  private renderSpotsBadge() {
    if (!this.event.max_attendees) return nothing;

    return html`
      <status-badge variant="scheduled" class="spots-badge">
        <ph-users-three slot="icon" size="12" weight="duotone"></ph-users-three>
        ${this.event.max_attendees}/${this.event.max_attendees} ${t("liveEventPreviewModal.spotsLeft")}
      </status-badge>
    `;
  }

  private formatDateTime(date: Date | string): string {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "numeric",
      hour12: true,
    };
    return dateObj.toLocaleDateString("en-US", options).replace(",", "");
  }

  private renderCtaButton() {
    const ctaText = this.event.cta_text || t("liveEventPreviewModal.defaultCta");

    return html`
      <button class="cta-button">
        <ph-ticket size="16"></ph-ticket>
        ${ctaText}
      </button>
    `;
  }

  protected override render() {
    const thumbnailStyle = this.thumbnailUrl ? `--bg-image: url(${this.thumbnailUrl})` : "";

    return html`
      <wow-modal mobile-fullscreen hide-primary-button @close=${this.handleClose}>
        <span slot="header">${this.getHeaderText()}</span>

        <div slot="one-column" class="preview-content">
          <div class="event-preview-card">
            <div class="event-thumbnail-container ${!this.thumbnailUrl ? "no-image" : ""}" style=${thumbnailStyle}>
              ${!this.thumbnailUrl ? html`<ph-image size="48" class="no-image-placeholder"></ph-image>` : nothing}
              ${this.renderPriceBadge()} ${this.renderSpotsBadge()}
              <div class="event-info-overlay">
                <p class="event-title">${this.event.title}</p>
                <span class="event-datetime">${this.formatDateTime(this.event.scheduled_start_time)}</span>
              </div>
            </div>
            <div class="event-details">
              ${this.event.description
                ? html`<expand-text class="event-description">${this.event.description}</expand-text>`
                : nothing}
              ${this.renderCtaButton()}
            </div>
          </div>

          <div class="action-buttons">
            <wow-button variant="neutral" size="medium" @click=${this.handleViewEventPage}>
              ${t("liveEventPreviewModal.viewEventPage")}
            </wow-button>
            <wow-button variant="primary" size="medium" @click=${this.handleShareWithAudience}>
              ${t("liveEventPreviewModal.shareWithAudience")}
              <ph-share size="16"></ph-share>
            </wow-button>
          </div>
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-event-preview-modal": LiveEventPreviewModal;
  }
}
